import { NetworkStatus, useQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { DEFAULT_PAGE_SIZE } from '../data/constants';
import { useDeepCompareCallback, useDeepCompareEffect } from 'use-deep-compare';

export const useInfiniteScrollQuery = ({ dataKey, query, config }) => {
  const { variables, skip, onCompleted } = config;
  const PAGE_SIZE = DEFAULT_PAGE_SIZE;

  const hasInitialCompleted = useRef(false);

  const { previousData, data, fetchMore, loading, error, networkStatus } =
    useQuery(query, {
      notifyOnNetworkStatusChange: true,
      variables: {
        ...variables,
        paging: { page: 0, pageSize: PAGE_SIZE },
      },
      onCompleted: (data) => {
        if (!hasInitialCompleted.current) {
          hasInitialCompleted.current = true;
          if (onCompleted) {
            onCompleted(data);
          }
        }
      },
      skip,
    });

  useDeepCompareEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [variables]);

  const { paging = {}, results = [] } =
    data?.[dataKey] || previousData?.[dataKey] || {};
  const { page = 0, total = 0 } = paging;

  const loadMore = useDeepCompareCallback(() => {
    if (loading) return;

    fetchMore({
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;
        if (onCompleted) {
          onCompleted(fetchMoreResult);
        }

        return {
          ...previousResult,
          [dataKey]: {
            ...fetchMoreResult[dataKey],
            results: [
              ...(previousResult?.[dataKey]?.results || []),
              ...(fetchMoreResult?.[dataKey]?.results || []),
            ],
          },
        };
      },
      variables: {
        ...variables,
        paging: { page: page + 1, pageSize: PAGE_SIZE },
      },
    });
  }, [loading, fetchMore, variables, page, PAGE_SIZE, onCompleted, dataKey]);

  const observer = useRef(null);

  const lastItemRef = useDeepCompareCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && results.length < total) {
          loadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, loadMore, results.length, total],
  );

  useEffect(() => {
    return () => observer.current?.disconnect();
  }, []);

  return {
    lastItemRef,
    loading: skip || networkStatus === NetworkStatus.loading,
    fetching: networkStatus === NetworkStatus.setVariables,
    loadingMore: networkStatus === NetworkStatus.fetchMore,
    results,
    error,
    total:
      data?.[dataKey]?.paging?.total ?? previousData?.[dataKey]?.paging?.total,
  };
};
